import Playback from '../../../State/Stores/Playback/Playback';
import arrayBufferToString from '../../Shared/Util/arrayBufferToString';

import ManifestError from '../Constants/ManifestError';

/**
 * Parses a provided HLS manifest to find the last contained segment
 * name, returning the full CDN path to the segment.
 */

const parseManifest = (manifestUrl: string, manifestContent: string): string => {
    const reSegmentName = /(\w+\.ts([0-9=m?]|)+(\n|$))/mg;
    const segmentNames = [];

    let matches: RegExpExecArray;

    while ((matches = reSegmentName.exec(manifestContent)) !== null) {
        segmentNames.push(matches[0]);
    }

    if (segmentNames.length < 1) throw new Error(ManifestError.NO_SEGMENTS_FOUND_IN_MANIFEST);

    const lastSegmentName = segmentNames[segmentNames.length - 1];
    const cdnSegments = manifestUrl.split('/');

    cdnSegments.pop();

    const lastSegmentUrl = [...cdnSegments, lastSegmentName].join('/');

    return lastSegmentUrl;
};

/**
 * Uses the resource timing API to find the last successful manifest request, and re-fetch it.
 *
 * If the request fails, we can assume a manifest-level failure, and an error is thrown
 * which can be handled by the caller.
 *
 * If the request succeeds, the manifest can be assumed to be functional, and it is handed
 * to `parseManifest` in order to extract and return the last contained segment URL in order
 * to then test the segment.
 */

const getLastSegmentUrlFromManifest = async ({media}: Playback): Promise<string> => {
    const resources = performance.getEntriesByType('resource')
        .filter((resource: PerformanceResourceTiming) =>
            resource.initiatorType === 'video' && resource.name.includes('.m3u8')
        );

    if (
        media.isLiveOrLinear && resources.length < 2 ||
        media.isVod && resources.length < 1
    ) throw new Error(ManifestError.NO_SUCCESSFUL_MANIFEST_REQUESTS_MADE);

    const lastManifestUrl: string = resources[resources.length - 1].name;

    const response = await fetch(lastManifestUrl);

    if (!response.ok) throw new Error(ManifestError.MANIFEST_REQUEST_FAILED);

    const lastManifestData = await response.arrayBuffer();

    const lastManifestContent = arrayBufferToString(lastManifestData);

    return parseManifest(lastManifestUrl, lastManifestContent);
};

export {
    getLastSegmentUrlFromManifest as default,
    parseManifest
};